import type { ReactElement, ReactNode } from 'react';

/** 時期フィルタ（会員の参加イベント・担当者の担当イベントで共通 / D-302・D-303） */
export type EventScheduleFilter = 'all' | 'upcoming' | 'past';

export const EVENT_SCHEDULE_OPTIONS: {
  value: EventScheduleFilter;
  label: string;
}[] = [
  { value: 'all', label: 'すべて' },
  { value: 'upcoming', label: 'これから' },
  { value: 'past', label: '終了したもの' },
];

export function eventMatchesSchedule(
  startsAt: string | null,
  schedule: EventScheduleFilter,
  nowMs: number = Date.now(),
): boolean {
  if (schedule === 'all') return true;
  // 日時未定（日程調整中）はこれから側（D-301）
  const isUpcoming = startsAt === null || new Date(startsAt).getTime() > nowMs;
  return schedule === 'upcoming' ? isUpcoming : !isUpcoming;
}

export interface EventTypeTab {
  name: string;
  count: number;
  sort: number;
}

/** 種別マスタ＋件数。0件の種別も残す（タブで見えるようにする） */
export function buildEventTypeTabs(
  counts: Map<string, number>,
  master: { name: string; sort_order: number; is_active: boolean }[] | undefined,
): EventTypeTab[] {
  const tabs = (master ?? [])
    .filter((t) => t.is_active)
    .map((t) => ({
      name: t.name,
      count: counts.get(t.name) ?? 0,
      sort: t.sort_order,
    }));
  for (const [name, count] of counts) {
    if (!tabs.some((t) => t.name === name)) {
      tabs.push({ name, count, sort: 9999 });
    }
  }
  if (tabs.length === 0) {
    return [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'ja'))
      .map(([name, count]) => ({ name, count, sort: 0 }));
  }
  return tabs.sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'ja'));
}

/**
 * ListTable と同じ | 区切りタブ。行ラベルで「時期」「種別」を並べ、二重の「すべて」を区別する。
 */
export function FilterTabRow({
  label,
  options,
  active,
  onChange,
}: {
  label: string;
  options: { value: string; label: string; count?: number | null }[];
  active: string;
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
      <span className="shrink-0 font-semibold text-wptext-muted">{label}</span>
      <ul className="flex flex-wrap items-center gap-x-2 text-wptext-muted">
        {options.map((opt, index) => (
          <li key={opt.value} className="flex items-center gap-2">
            {index > 0 && <span aria-hidden="true">|</span>}
            <button
              type="button"
              aria-current={active === opt.value ? 'page' : undefined}
              onClick={() => onChange(opt.value)}
              className={
                active === opt.value
                  ? 'font-semibold text-wptext-strong'
                  : 'text-wpaccent hover:text-wpaccent-hover'
              }
            >
              {opt.label}
              {opt.count != null && (
                <span className="ml-1 text-wptext-muted">({opt.count})</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EventTypeSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="mb-6 last:mb-0">
      <h3 className="mb-2.5 font-semibold text-wptext-strong">
        {title}
        <span className="ml-2 font-normal text-wptext-muted">{count}件</span>
      </h3>
      {children}
    </section>
  );
}
